import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { FileText, Paperclip, Sidebar } from '@lifeomic/chromicons';
import moment from 'moment';
import React, { useMemo } from 'react';
import {
  useDisableAnimations,
  useLocalStorageState,
} from '../../../../../hooks';
import {
  PresentationAction,
  PresentationModal,
  PresentationModalProps,
} from '../../../../Presentation';
import { useStyles } from '../../ExecutiveSummary/Presentation';
import { ReferenceImageMenu } from '../../ExecutiveSummary/ReferenceImageMenu';
import {
  AddAttachmentPopover,
  AttachmentsPopover,
} from '../Attachments/AttachmentList';
import { ViewMode } from '../Attachments/InlineAttachment';
import { ImpressionItem } from '../ImpressionEditor/ImpressionItem';

interface ReportPresentationProps
  extends Omit<PresentationModalProps, 'actions'> {
  patient: fhir.Patient;
  impression: ImpressionItem;
  viewMode: ViewMode;
  children: React.ReactNode;
}

const calculateAge = (patient: fhir.Patient): number | undefined => {
  return patient.birthDate
    ? moment().diff(patient.birthDate, 'years')
    : undefined;
};

const getMembershipTier = (patient: fhir.Patient): string | undefined => {
  return patient.meta?.tag
    ?.find(
      tag =>
        tag.system === 'http://lifeomic.com/fhir/fountain-life-membership-tier',
    )
    ?.code?.toUpperCase();
};

export const ReportPresentation: React.FC<ReportPresentationProps> = ({
  patient,
  impression,
  viewMode,
  children,
  ...modalProps
}) => {
  const disableAnimations = useDisableAnimations();
  const classes = useStyles({ viewMode, disableAnimations });
  const [showSidebars, setShowSidebars] = useLocalStorageState(
    'zori-presentation-view-sidebar',
    false,
  );

  const age = calculateAge(patient);
  const membershipTier = getMembershipTier(patient);
  const attachments = useMemo(
    () => impression.visibleAttachments,
    [impression],
  );

  return (
    <PresentationModal
      {...modalProps}
      viewMode={viewMode}
      actions={
        viewMode === 'print' ? null : (
          <>
            <PresentationAction
              title={showSidebars ? 'Hide Sidebars' : 'Show Sidebars'}
              className={classes.toggleSidebarButton}
              icon={Sidebar}
              onClick={() => setShowSidebars(!showSidebars)}
            />
            <AddAttachmentPopover
              action="read-reference"
              patient={patient}
              onUseAttachment={() => undefined}
              placement="right-start"
              anchorElement={
                <PresentationAction title="All Attachments" icon={FileText} />
              }
            />
            <AttachmentsPopover
              isLoading={false}
              action="readonly"
              allAttachments={attachments}
              onUseAttachment={() => undefined}
              placement="right-start"
              anchorElement={
                <PresentationAction
                  title="Section Attachments"
                  icon={Paperclip}
                />
              }
            />
            <ReferenceImageMenu impressionType={impression.impressionType} />
          </>
        )
      }
    >
      <Box className={classes.overflow}>{children}</Box>
      {viewMode !== 'print' && showSidebars && (
        <Box gap={1.5}>
          <Box
            className={classes.secondarySidebarsBox}
            direction="column"
            gap={1.5}
          >
            <Box className={classes.sidebar} direction="column">
              <Box className={classes.sidebarHeader}>
                <Text size="body">{`${patient.name?.[0]?.given?.join(' ')} ${
                  patient.name?.[0]?.family ?? ''
                }`}</Text>
              </Box>
              <Box
                className={classes.patientInfo}
                paddingX={2}
                paddingTop={1}
                paddingBottom={1.5}
              >
                <Text size="subbody">Age</Text>
                <Text size="subbody">{age}</Text>
                {impression.reportDate && (
                  <>
                    <Text size="subbody">Report Date</Text>
                    <Text size="subbody">{impression.reportDate}</Text>
                  </>
                )}
                <Text size="subbody">Membership Tier</Text>
                <Text size="subbody">{membershipTier}</Text>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </PresentationModal>
  );
};
